import {
  Box,
  Chip,
  FormControl,
  Grid,
  Input,
  MenuItem,
  Modal,
  Select,
  TextField,
  Typography,
  Checkbox,
  ListItemText,
} from "@mui/material";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import { Form, Formik } from "formik";
import { postAPICall } from "lib/callApi";
import { CHECK_IF_KPI_TEMPLATE_NAME_IS_UNIQUE, CREATE_KPI_TEMPLATE } from "lib/constants";
import { useState } from "react";
import * as Yup from "yup";
import EditKPITable from "./EditKPITable";

function CreateKPITemplateModal({
  style,
  open,
  handleClose,
  templateDetail,
  kpiActivities,
  refreshKpiTemplates,
  unassignedUsers,
  showNotification,
  isMobileDisplay,
}) {
  const newTemplate = templateDetail === undefined;
  const submitButtonText = newTemplate ? "Create" : "Copy";
  const [isRequestApiLoading, setIsRequestApiLoading] = useState(false);
  const isTemplateNameUnique = async (newName) => {
    try {
      let isUnique = false;
      const response = await postAPICall(CHECK_IF_KPI_TEMPLATE_NAME_IS_UNIQUE, true, {
        name: newName,
      });
      if (response?.data?.success) {
        isUnique = response.data.data.isUnique;
      }
      return isUnique;
    } catch (error) {
      return false;
    }
  };

  // eslint-disable-next-line func-names
  Yup.addMethod(Yup.array, "unique", function (message, mapper = (a) => a) {
    // eslint-disable-next-line react/no-this-in-sfc
    return this.test("unique", message, (list) => list.length === new Set(list.map(mapper)).size);
  });
  const validationSchema = Yup.object({
    name: Yup.string()
      .max(30, "Name cannot be more than 30 characters")
      .required("Name is required")
      .test("unique-name", "KPI template with that name already exists", async (value) => {
        const isUnique = await isTemplateNameUnique(value);
        if (!isUnique) {
          throw new Yup.ValidationError(
            "KPI template with that name already exists",
            value,
            "name"
          );
        }
        return true;
      }),
    description: Yup.string().max(255, "Description cannot be more than 255 characters"),
    kpiActivities: Yup.array()
      .of(
        Yup.object().shape({
          id: Yup.number().nullable(),
          name: Yup.string().required("Please select KPI").typeError("Please select KPI"),
          points: Yup.number()
            .min(1, "Invalid point value")
            .max(999, "Invalid point value")
            .typeError("Invalid point value"),
          count: Yup.number()
            .min(1, "Invalid KPI value")
            .max(999, "Invalid KPI value")
            .typeError("Invalid KPI value"),
        })
      )
      .unique("New KPI names cannot be same.", (a) => a?.name?.trim())
      .required("Please select KPI Activity"),
  });

  const handleSubmit = async (data) => {
    setIsRequestApiLoading(true);
    try {
      const response = await postAPICall(CREATE_KPI_TEMPLATE, true, data);
      if (response?.data?.success) {
        showNotification("success", "Template copied successfully!");
        refreshKpiTemplates();
      } else {
        showNotification("error", "Could not copy template, please try again!");
      }
    } catch (error) {
      showNotification("error", "Could not copy template, please try again!");
    } finally {
      setIsRequestApiLoading(false);
      handleClose();
    }
  };

  const initialValues = newTemplate
    ? {
        name: "",
        description: "",
        kpiActivities: [{ id: null, points: 1, name: "", count: 1, new: false }],
        assignedUsers: [],
      }
    : {
        name: `Copy of ${templateDetail.template.name}`,
        description: templateDetail.template.description,
        kpiActivities: templateDetail.activitiesWithPoints.map((kpi) => ({
          ...kpi,
          name: kpi?.KpiActivity?.name,
          id: kpi?.KpiActivity?.id,
        })) || [{ id: null, points: 1, name: "", count: 1, new: false }],
        assignedUsers: [],
      };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
    >
      <Box sx={style}>
        <Box sx={{ mb: "1.5rem" }}>
          <Typography variant="h4" sx={{ mb: "0.5rem" }}>
            {newTemplate ? "Create New KPI Template" : "Copy KPI Template"}
          </Typography>
          <Typography variant="h6" sx={{ fontWeight: "400" }}>
            {newTemplate
              ? "Select the KPIs for this template"
              : `Copying template ${templateDetail.template.name}`}
          </Typography>
        </Box>
        <Formik
          initialValues={initialValues}
          enableReinitialize
          validateOnBlur
          validateOnChange={false}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
        >
          {({ values, setFieldValue, touched, errors, setValues }) => (
            <Form>
              <Box sx={{ flexGrow: 1 }}>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Template Name"
                      variant="standard"
                      defaultValue={values.name}
                      onChange={(event) => setFieldValue("name", event.target.value)}
                      error={Boolean(touched.name && errors.name)}
                      helperText={touched.name && errors.name}
                      // sx={{ width: isMobileDisplay ? "100%" : "50%" }}
                      sx={{ width: "100%" }}
                      inputProps={{ maxLength: 30 }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Description"
                      variant="standard"
                      defaultValue={values.description}
                      onChange={(event) => setFieldValue("description", event.target.value)}
                      error={Boolean(touched.description && errors.description)}
                      helperText={touched.description && errors.description}
                      // sx={{ width: isMobileDisplay ? "100%" : "50%" }}
                      sx={{ width: "100%" }}
                      inputProps={{ maxLength: 255 }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <FormControl sx={{ width: "100%", margin: "1rem 0rem", display: "flex" }}>
                      <Typography sx={{ fontSize: "0.8rem", color: "#7b809a" }}>
                        Assign KPI Template to
                      </Typography>
                      <Select
                        multiple
                        sx={{
                          width: "100%",
                          padding: "0.5rem 0",
                          flex: "1",
                          "& .MuiSelect-select": {
                            padding: "0 !important",
                          },
                        }}
                        value={values.assignedUsers}
                        onChange={(a, b) => {
                          let arr = values.assignedUsers;
                          if (arr.map((_) => _.id).includes(b.props.value.userId)) {
                            arr = arr.filter((_) => _.id !== b.props.value.userId);
                          } else {
                            arr.push({
                              id: b.props.value.userId,
                              name: b.props.value.name,
                              email: b.props.value.email,
                              disabled: b.props.value.disabled,
                            });
                          }
                          setFieldValue("assignedUsers", arr);
                        }}
                        input={<Input />}
                        renderValue={(selected) => (
                          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                            {selected.map((value) => (
                              <Chip
                                onMouseDown={(event) => {
                                  event.stopPropagation();
                                }}
                                key={value.id}
                                label={value.name}
                                disabled={!!value.disabled}
                                onDelete={() => {
                                  const arr = values.assignedUsers.filter(
                                    (user) => user.id !== value.id
                                  );
                                  setFieldValue("assignedUsers", arr);
                                }}
                              />
                            ))}
                          </Box>
                        )}
                      >
                        {unassignedUsers
                          .map((user) => (
                            <MenuItem key={user.userId} value={user}>
                              <Checkbox
                                checked={values.assignedUsers.some((_) => _.id === user.userId)}
                              />
                              <ListItemText primary={user.name} />
                            </MenuItem>
                          ))
                          .sort((a, b) => (a.name > b.name ? 1 : -1))}
                      </Select>
                    </FormControl>
                  </Grid>
                </Grid>
              </Box>
              <Box sx={{ mt: "1.75rem" }}>
                <EditKPITable
                  kpiActivities={kpiActivities}
                  setFieldValue={setFieldValue}
                  setValues={setValues}
                  values={values}
                  touched={touched}
                  errors={errors}
                  isMobileDisplay={isMobileDisplay}
                />
              </Box>
              <MDBox mt={2} sx={{ display: "flex", justifyContent: "flex-end" }}>
                <MDButton onClick={handleClose} color="gray80" style={{ marginRight: "10px" }}>
                  Cancel
                </MDButton>
                <MDButton type="submit" color="primaryRed" disabled={isRequestApiLoading}>
                  {isRequestApiLoading ? "Loading..." : submitButtonText}
                </MDButton>
              </MDBox>
            </Form>
          )}
        </Formik>
      </Box>
    </Modal>
  );
}

export default CreateKPITemplateModal;
